import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Divider,
} from '@chakra-ui/react'
import { useFormContext } from 'react-hook-form'
import { RequestFormData } from '..'
import SearchUsersComponent from '../../../Forms/components/SearchUsersComponent'
import { ChangeEvent, useState } from 'react'
import { FaUserPlus, FaCheckCircle, FaEnvelope } from 'react-icons/fa'
import { UserAround } from '../../../../api/api_hooks/user/useGetUsersAroundPoint'
import { useTranslation } from 'react-i18next'

interface DoctorSelectionStepProps {
  setReceiverDoctorDisplayName: (name: string | null) => void
  resetKey?: number
}

export default function DoctorSelectionStep({
  setReceiverDoctorDisplayName,
  resetKey,
}: DoctorSelectionStepProps) {
  const { t } = useTranslation()
  const {
    setValue,
    formState: { errors },
  } = useFormContext<RequestFormData>()

  const [selectedUser, setSelectedUser] = useState<UserAround | null>(null)
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(
    null
  )

  const selectUser = (user: UserAround | null) => {
    if (!user) {
      return
    }
    setSelectedUser(user)
    setValue('receiverEmail', user.email)
    setSelectedUserEmail(null)
    setReceiverDoctorDisplayName(user.contactName)
  }

  const selectEmail = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const email = e.target.value
    setSelectedUserEmail(email)
    setValue('receiverEmail', email)
    setReceiverDoctorDisplayName(email)
    setSelectedUser(null)
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Map and correspondent selection */}
      <Box>
        <SearchUsersComponent
          key={resetKey}
          selectedUser={selectedUser}
          setSelectedUser={selectUser}
        />
      </Box>

      {/* Divider */}
      <Box position="relative" py={3}>
        <Divider borderColor="gray.300" />
        <Text
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="white"
          px={4}
          fontSize="sm"
          color="gray.500"
          fontWeight="medium"
        >
          {t('shared.common.or') || 'ou'}
        </Text>
      </Box>

      {/* Invite colleague section */}
      <Box
        bg="white"
        p={5}
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <VStack spacing={4} align="stretch">
          {/* Header section */}
          <HStack spacing={3} align="start">
            <Box
              p={2}
              bg="purple.50"
              borderRadius="lg"
              color="purple.600"
              flexShrink={0}
            >
              <Icon as={FaUserPlus} boxSize={5} />
            </Box>
            <Box flex={1}>
              <Text fontWeight="semibold" fontSize="md" color="gray.800" mb={1}>
                {t('sharedDiagnostic.inviteColleaguesTitle')}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {t('sharedDiagnostic.inviteColleaguesDescription')}
              </Text>
            </Box>
          </HStack>

          {/* Email input */}
          <FormControl isInvalid={!!errors.receiverEmail}>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
              {t('sharedDiagnostic.colleagueEmail')}
            </FormLabel>
            <Input
              type="email"
              placeholder={t('sharedDiagnostic.colleagueEmailPlaceholder')}
              onChange={selectEmail}
              value={selectedUserEmail ?? ''}
              size="md"
              borderRadius="lg"
              borderColor="gray.300"
              bg="white"
              _hover={{ borderColor: 'purple.300' }}
              _focus={{
                borderColor: 'purple.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
                bg: 'white',
              }}
            />
            {errors.receiverEmail && (
              <FormErrorMessage mt={2}>
                <Icon as={FaEnvelope} mr={2} />
                {errors.receiverEmail?.message}
              </FormErrorMessage>
            )}
            {!errors.receiverEmail && selectedUserEmail && (
              <HStack mt={3} p={3} bg="green.50" borderRadius="lg" border="1px solid" borderColor="green.200">
                <Icon as={FaCheckCircle} color="green.600" boxSize={4} />
                <Text fontSize="sm" color="green.700" fontWeight="medium">
                  {t('sharedDiagnostic.invitedByEmail') || 'Invitation par email'}: {selectedUserEmail}
                </Text>
              </HStack>
            )}
          </FormControl>
        </VStack>
      </Box>
    </VStack>
  )
}
